import { HttpClient } from '@angular/common/http';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { ClientPurchaseOrderService } from '@app/core/client/services/client-purchase-order.service';
import { ClientService } from '@app/core/client/services/client.service';
import { PricingStructureTemplateService } from '@app/core/client/services/pricing-structure-template.service';
import { Client } from '@app/core/client/store/client.model';
import { LocationService } from '@app/core/locations/services/location.service';
import { ProductCategoryService } from '@app/core/product-categories/services/product-category.service';
import { ProductTagService } from '@app/core/product-tags/services/product-tag.service';
import { RfqService, RFQ } from '@app/core/quotes';
import { StaffService, Staff } from '@app/core/staff';
import { environment } from '@app/env-service';
import { SetDiscountStructureComponent as RfqDiscountStructurePopup } from '@app/modules/inventory/set-discount-structure/set-discount-structure.component';
import { SetMarkupStructureComponent as RfqMarkupStructurePopup } from '@app/modules/inventory/set-markup-structure/set-markup-structure.component';
import { ClientFormComponent } from '@app/shared/components/client-form/client-form.component';

@Component({
  selector: 'cnc-client-purchase-order-popup-form',
  templateUrl: './client-purchase-order-popup-form.component.html',
  styleUrls: ['./client-purchase-order-popup-form.component.scss'],
})
export class ClientPurchaseOrderPopupFormComponent implements OnInit {
  url :string = environment.url;

  searchFailed :boolean = false;
  searching :boolean = false;

  client$ :Observable<any[]>;
  clientLoading = false;
  clientInput$ = new Subject<string>();

  total :any = {
    units: 0,
    amount: 0,
    discount: 0,
  };

  loading :boolean = false;
  submitted :boolean = false;
  formData :UntypedFormGroup;

  is_overridden :boolean = false;

  pageTitle :string = 'Create Client Purchase Order';

  productCategories :any[] = [];
  productSubCategories :any[] = [];
  warehouses :any[] = [];

  deliveryTypeLookUp :any[] = [
    {
      label: 'Standard',
      value: 'standard',
    },
    {
      label: 'Warehouse Pickup',
      value: 'warehouse',
    },
    {
      label: 'Local Delivery',
      value: 'local_delivery',
    },
  ];

  originalLineItems = [];

  pricingStructure :any;

  @Input() kitRfqs :any;
  @Input() purchaseOrder :any;
  @Input() isFetchingKitsRfqs :any;

  rfq$ :Observable<any[]>;
  rfqLoading = false;
  rfqInput$ = new Subject<string>();

  dynamicLineItems :any = [];

  price_structure :string = 'Discount';

  private readonly _kitRfqs = new BehaviorSubject<any>(<any>[]);
  public kitRfqs$ = this._kitRfqs.asObservable();

  tags :any = [];
  selectedTags :any = [];

  constructor(
    public activeModal :NgbActiveModal,
    private fb :UntypedFormBuilder,
    private toastr :ToastrService,
    private translate :TranslateService,
    public staffService :StaffService,
    public authService :AuthService,
    private modal :NgbModal,
    public clientSvc :ClientService,
    private clientPurchaseOrderSvc :ClientPurchaseOrderService,
    private productCategorySvc :ProductCategoryService,
    private rfqSvc :RfqService,
    private cd :ChangeDetectorRef,
    public tagSvc :ProductTagService,
    public structureService :PricingStructureTemplateService,
    private http :HttpClient,
    public locationSvc :LocationService,
  ) {}

  ngOnInit() {
    this.pageTitle = this.translate.instant('TITLES.CREATE_CLIENT_PURCHASE_ORDER');
    this.getWarehouses();
    this.loadClient();

    this.tagSvc.getTags({ parent_only: true }, null);
    this.tagSvc.formattedRecord$.subscribe((tags :any) => {
      this.tags = tags;
    });

    this.productCategorySvc.record$.subscribe((categories :any) => {
      this.productCategories = categories;
      this.productSubCategories = [];
      categories.forEach((category :any) => {
        this.productSubCategories = this.productSubCategories.concat(
          category.sub_categories,
        );
      });
    });

    const dateToday = new Date();

    this.formData = this.fb.group({
      client: [null, Validators.required],
      order_date: [dateToday.toISOString().substr(0, 10), Validators.required],
      line_items: this.fb.array([]),
      ship_address: [''],
      ship_city: [''],
      ship_state: [''],
      ship_zip: [''],
      ship_country: [''],
      overridden: [0],
      will_affect_inventory: [0],
      tags: [[]],
      po_type: ['one_time'],
      is_limited_by_occurrence: [0],
      max_occurrences: [''],
      recurring_type: ['day'],
      day_weekly: [''],
      day_monthly: [''],
      day_annually: [''],
      pricing_setting: ['default'],
      delivery_type: ['standard'],
      warehouse_id: [0],
    });

    if (this.purchaseOrder && this.purchaseOrder.id) {
      this.formData.patchValue(this.purchaseOrder);
      this.formData.patchValue({
        tags: this.purchaseOrder.tags.map((e) => e.id),
      });
      this.pageTitle = this.translate.instant(
        'TITLES.UPDATE_CLIENT_PURCHASE_ORDER',
      );
      this.purchaseOrder.line_items.forEach((item :any) => {
        const row :any = {
          id: item.id,
          item: {
            id: item.item_id,
            is_kit: item.is_kit,
            sku: item.sku,
            product_name: item.product_name,
            discount_structure:
              item.item_id > 0 ? item.item.discount_structure : null,
          },
          qty: item.quantity,
          price: item.unit_price,
          discount: item.discount_amount,
          discount_percent:
            (item.discount_amount / (item.quantity * item.unit_price)) * 100,
        };
        this.addItem(row);

        if (row.item.id == 0) this.dynamicLineItems.push(row.item);

        // this.total.amount += item.quantity * item.unit_price;
        // this.total.discount += item.discount_amount;
      });
      this.is_overridden = this.purchaseOrder.overridden;
      this.loadTemplate(this.purchaseOrder.client_id);
    }

    this.loadRfqs();
    this.rfq$.subscribe((data) => {
      this._kitRfqs.next(data);
    });
  }

  onSubmit(status :string) {
    this.submitted = true;
    if (this.formData.invalid) {
      Swal.fire({
        title: 'Form Error!',
        text: 'Please fill all the required fields.',
        type: 'error',
        confirmButtonText: 'Back to Form',
      });

      return;
    }

    this.loading = true;
    const payload :any = Object.assign({}, this.formData.value);
    (payload.client_id = payload.client.id), delete payload.client;
    payload.line_items = [];

    let totalAmount :number = 0;
    let totalUnits :number = 0;
    let totalDiscount :number = 0;
    this.formData.value.line_items.forEach((rowItem :any) => {
      if (rowItem.item && rowItem.qty > 0) {
        payload.line_items.push({
          id: rowItem.id,
          item_id: rowItem.item.id,
          sku: rowItem.item.sku,
          product_name: rowItem.item.product_name,
          is_kit: rowItem.item.is_kit,
          quantity: rowItem.qty,
          unit_price: rowItem.price,
          discount_amount: rowItem.discount,
        });
        totalUnits += rowItem.qty;
        totalAmount += rowItem.line_total;
        totalDiscount += rowItem.discount;
      }
    });
    payload.total_units = totalUnits;
    payload.total_amount = totalAmount;
    payload.discount_amount = totalDiscount;
    payload.order_status = status;
    payload.overridden = this.formData.value.overridden;

    if (!payload.line_items || payload.line_items.length == 0) {
      Swal.fire({
        title: 'Form Error!',
        text: 'Please include at least one(1) line item.',
        type: 'error',
        confirmButtonText: 'Back to Form',
      });

      return;
    }

    if (this.purchaseOrder && this.purchaseOrder.id) {
      this.checkValidPurchaseId(this.purchaseOrder.id);

      this.clientPurchaseOrderSvc
        .updatepurchaseOrder(this.purchaseOrder.id, payload)
        .subscribe(
          (resp) => {
            this.toastr.success('Purchase order updated.');
            this.loading = false;
            this.activeModal.close(resp);
          },
          (error) => {
            this.loading = false;
            this.toastr.error(
              'Error saving purchase order. Please try again later or contact support.',
            );
          },
        );
    } else {
      this.clientPurchaseOrderSvc.createpurchaseOrder(payload).subscribe(
        (resp) => {
          this.toastr.success('New purchase order created.');
          this.loading = false;
          this.activeModal.close(resp);
        },
        (error) => {
          this.loading = false;
          this.toastr.error(
            'Error creating purchase. Please try again later or contact support.',
          );
        },
      );
    }
  }

  async setAddress() {
    const client :any = this.formData.value.client;
    if (client) {
      this.formData.get('ship_address').patchValue(client.shipping_address);
      this.formData.get('ship_city').patchValue(client.shipping_city);
      this.formData.get('ship_state').patchValue(client.shipping_state);
      this.formData.get('ship_zip').patchValue(client.shipping_zip);
      this.formData.get('ship_country').patchValue(client.shipping_country);
      this.pricingStructure = await this.loadTemplate(client.id);
    }
  }

  setPoType() {
    this.formData.get('day_weekly').setValidators([]);
    this.formData.get('day_monthly').setValidators([]);
    this.formData.get('day_annually').setValidators([]);

    if (this.formData.value.recurring_type == 'week') {
      this.formData.get('day_weekly').setValidators([Validators.required]);
    }

    if (this.formData.value.recurring_type == 'month') {
      this.formData.get('day_monthly').setValidators([Validators.required]);
    }

    if (this.formData.value.recurring_type == 'year') {
      this.formData.get('day_annually').setValidators([Validators.required]);
    }

    this.formData.get('day_weekly').updateValueAndValidity({
      onlySelf: true,
    });
    this.formData.get('day_monthly').updateValueAndValidity({
      onlySelf: true,
    });
    this.formData.get('day_annually').updateValueAndValidity({
      onlySelf: true,
    });
  }

  changeRecurringEnding() {
    this.formData.get('max_occurrences').setValidators([]);

    if (this.formData.value.is_limited_by_occurrence == 1) {
      this.formData.get('max_occurrences').setValidators([Validators.required]);
    }
    this.formData.get('max_occurrences').updateValueAndValidity({
      onlySelf: true,
    });
  }

  computeLineTotal(idx :number, formFieldName) {
    const lineItems :UntypedFormArray = this.formData.get(
      'line_items',
    ) as UntypedFormArray;
    const lineItem :any = lineItems.at(idx).value;
    const newTotal :number = lineItem.qty * lineItem.price;
    const discount :number = this.getDiscountAmount(
      lineItem.item,
      lineItem.qty,
      newTotal,
    );
    const lineTotal :number =
      newTotal + discount * (this.price_structure == 'Discount' ? -1 : 1);
    this.total.amount = this.total.amount - lineItem.line_total + lineTotal;
    this.total.discount = this.total.discount - lineItem.discount + discount;
    lineItems.at(idx).patchValue({
      discount: discount,
      line_total: lineTotal,
      discount_percent: (discount / newTotal) * 100,
    });

    if (formFieldName == 'qty') {
      this.originalLineItems[idx].qty = lineItem.qty;
      this.originalLineItems[idx].discount = discount;
      this.originalLineItems[idx].line_total = lineTotal;
      this.originalLineItems[idx].discount_percent = (discount / newTotal) * 100;
    }

    if (formFieldName == 'price' && this.is_overridden) {
      this.formData.get('overridden').patchValue(true);
    }
  }

  addItem(item ?:any) {
    const lineItems :UntypedFormArray = this.formData.get(
      'line_items',
    ) as UntypedFormArray;
    if (item) {
      const price =
        this.price_structure == 'Discount'
          ? item.price
          : this.getMaterialCost(item.item);
      const lineTotal :number =
        item.qty * price +
        item.discount * (this.price_structure == 'Discount' ? -1 : 1); // add if markup, subtract if discount ;
      lineItems.push(
        this.fb.group({
          id: [item.id],
          item: [item.item, Validators.required],
          qty: [item.qty, Validators.required],
          price: [price, Validators.required],
          line_total: lineTotal,
          discount: [item.discount],
          discount_percent: [item.discount_percent],
        }),
      );

      // this.originalLineItems.push({
      // 	id: item.id,
      // 	item: item.item,
      // 	qty: item.qty,
      // 	price: price,
      // 	line_total: lineTotal,
      // 	discount: item.discount,
      // 	discount_percent: item.discount_percent,
      // });

      this.total.amount += lineTotal;
      this.total.discount += item.discount;
    } else {
      lineItems.push(
        this.fb.group({
          id: [0],
          item: [null, Validators.required],
          qty: [1, Validators.required],
          price: [0, Validators.required],
          line_total: [0],
          discount: [0],
          discount_percent: [null],
        }),
      );
    }
  }

  getDiscountAmount(item :any, quantity :number, lineTotal :number) {
    if (!item || !item.id) {
      return 0;
    }

    let amount = -1;
    const client :any = this.formData.value.client;

    if (client && client.pricing_structure == 'markup_structure') {
      const material_cost = this.getMaterialCost(item);
      amount =
        (this.getDiscountPercent(this.getPriceStructure(item), quantity) *
          (quantity * material_cost)) /
        100;
    } else if (this.getPriceStructure(item)) {
      // discount structure default
      amount =
        (this.getDiscountPercent(this.getPriceStructure(item), quantity) *
          lineTotal) /
        100;
    }

    return amount < 0 ? 0 : amount;
  }

  getMaterialCost(item) {
    let material_cost = 0;
    if (item.is_kit && item.base_price) {
      // kits
      material_cost =
        item.base_price.computed_fifo > 0 ? item.base_price.computed_fifo : 0;
      material_cost =
        material_cost > 0 ? material_cost : item.base_price.computed_weighted;
      material_cost =
        material_cost > 0 ? material_cost : item.base_price.computed_last_po;
    } else if (!item.is_kit && item.base_price) {
      // sku
      material_cost =
        item.base_price.computed_fifo > 0 ? item.base_price.computed_fifo : 0;
      material_cost =
        material_cost > 0 ? material_cost : item.base_price.computed_weighted;
      material_cost =
        material_cost > 0 ? material_cost : item.base_price.computed_last_po;
    }

    return material_cost;
  }

  getDiscountPercent(discountStructure, quantity) {
    if (!discountStructure) {
      return -1;
    }
    try {
      discountStructure = JSON.parse(discountStructure);
    } catch (e) {}
    discountStructure = discountStructure.sort((a, b) => a.min - b.min);
    for (let i = 0; i < discountStructure.length; i++) {
      if (
        quantity >= discountStructure[i].min &&
        quantity <= discountStructure[i].max
      ) {
        return discountStructure[i].discount;
      }
    }

    return -1;
  }

  removeItem(idx :any) {
    const lineItems :UntypedFormArray = this.formData.get(
      'line_items',
    ) as UntypedFormArray;
    lineItems.removeAt(idx);
    this.originalLineItems.splice(idx, 1);
  }

  trackClientByFn(item :any) {
    return item.id;
  }

  private loadClient() {
    this.client$ = concat(
      this.clientSvc.search(''), // default items
      this.clientInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.clientLoading = true)),
        switchMap((term) =>
          this.clientSvc.search(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.clientLoading = false)),
          ),
        ),
      ),
    );
  }

  setPrice(idx :number) {
    const lineItems :UntypedFormArray = this.formData.get(
      'line_items',
    ) as UntypedFormArray;
    const lineItem :any = this.formData.value.line_items[idx];
    const price =
      this.price_structure == 'Discount'
        ? lineItem.item.unit_price
        : this.getMaterialCost(lineItem.item);
    this.originalLineItems.push(lineItem);
    if (price >= 0) {
      lineItems.at(idx).patchValue({ price: price });
      this.originalLineItems[idx].price = price;
      this.computeLineTotal(idx, 'qty');
    }
  }

  formatter = (client :any) => {
    if (client && client.id) {
      if (client.user) {
        return (
          client.business_name +
          '  [' +
          [client.user.firstname, client.user.lastname].join(' ') +
          ']'
        );
      }

      return client.business_name;
    }

    return '';
  };

  search = (text$ :Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap((term) =>
        this.clientSvc.search(term).pipe(
          tap(() => (this.searchFailed = false)),
          catchError(() => {
            this.searchFailed = true;

            return of([]);
          }),
        ),
      ),
      tap(() => (this.searching = false)),
    );

  searchFn(term :string, item :any) {
    return (
      item.sku.toLowerCase().indexOf(term.toLocaleLowerCase()) >= 0 ||
      item.product_name.toLocaleLowerCase().indexOf(term.toLocaleLowerCase()) >= 0
    );
  }

  newClient() {
    const modalRef = this.modal.open(ClientFormComponent, {
      size: 'lg',
    });
    modalRef.result.then((client) => {
      if (client && client.id) {
        this.formData.patchValue({
          client: client,
        });
        this.setAddress();
      }
    });
  }

  trackByFn(item :any) {
    return item.id;
  }

  private loadRfqs() {
    this.rfq$ = concat(
      of([...this.dynamicLineItems]), // default items
      this.rfqInput$.pipe(
        distinctUntilChanged(),
        debounceTime(1000),
        tap(() => (this.rfqLoading = true)),
        switchMap((term) =>
          this.rfqSvc
            .searchSKU({
              sku: term,
              kit: 1,
              allAlternateSkus: 0,
              is_discontinued: 2,
            })
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.rfqLoading = false)),
            ),
        ),
      ),
    );
  }

  setDiscountStructure(row :any, idx :number) {
    let modalRef :any;

    modalRef = this.modal.open(RfqDiscountStructurePopup, {
      size: 'lg',
    });
    modalRef.componentInstance.rfq = row.item;

    modalRef.result.then(
      (resp :any) => {
        if (resp && resp.id) {
          const lineItems :UntypedFormArray = this.formData.get(
            'line_items',
          ) as UntypedFormArray;
          const lineItem :any = this.formData.value.line_items[idx];
          lineItems.at(idx).patchValue({
            ...lineItem,
            item: {
              ...lineItem.item,
              discount_structure: resp.discount_structure,
            },
          });
          this.cd.detectChanges();
        }
      },
      (_) => {},
    );
  }

  setMarkupStructure(row :any, idx :number) {
    let modalRef :any;

    modalRef = this.modal.open(RfqMarkupStructurePopup, {
      size: 'lg',
    });
    modalRef.componentInstance.rfq = row.item;

    modalRef.result.then(
      (resp :any) => {
        if (resp && resp.id) {
          const lineItems :UntypedFormArray = this.formData.get(
            'line_items',
          ) as UntypedFormArray;
          const lineItem :any = this.formData.value.line_items[idx];
          lineItems.at(idx).patchValue({
            ...lineItem,
            item: {
              ...lineItem.item,
              markup_structure: resp.markup_structure,
            },
          });
          this.cd.detectChanges();
        }
      },
      (_) => {},
    );
  }

  setDiscountManually(idx :number) {
    const lineItems :any = this.formData.get('line_items') as UntypedFormArray;
    const lineItem :any = lineItems.at(idx).value;
    const newTotal :number = lineItem.qty * lineItem.price;
    const discount :number = lineItem.discount;
    const lineTotal :number =
      newTotal + discount * (this.price_structure == 'Discount' ? -1 : 1);
    this.total.amount = this.total.amount - lineItem.line_total + lineTotal;
    this.total.discount = 0;
    lineItems.controls.forEach((item :any) => {
      // console.log(item.get('discount'));
      this.total.discount += item.get('discount').value;
    });
    lineItems.at(idx).patchValue({
      discount: discount,
      line_total: lineTotal,
      discount_percent: (discount / newTotal) * 100,
    });
    this.cd.detectChanges();

    this.formData.get('overridden').patchValue(true);
  }

  addTagFn(idx :number) {
    return (term) => {
      return {
        id: 0,
        product_name: term,
        item: { id: 0, product_name: term },
        qty: 1,
        price: 0,
        discount: 0,
        discount_percent: 0,
      };
    };
  }

  getPriceStructure(item :any) {
    const client :any = this.formData.value.client;
    const key = client ? client.pricing_structure : 'discount_structure';

    if (client && this.pricingStructure) {
      for (let i = 0; i < this.pricingStructure.length; i++) {
        const structure :any = this.pricingStructure[i];
        if (structure.slug == 'client') {
          if (client && client[key]) {
            return client[key];
          }
        }

        if (structure.slug == 'client_type') {
          if (
            client &&
            client.client_type &&
            client.client_type[key] &&
            client.client_type[key] > 0
          ) {
            return client.client_type[key];
          }
        }

        if (structure.slug == 'sku') {
          if (item[key] && item[key].length > 0) {
            return item[key];
          }
        }

        if (structure.slug == 'product_sub_category') {
          if (
            item.subcategory &&
            item.subcategory[key] &&
            item.subcategory[key].length > 0
          ) {
            return item.subcategory[key];
          }
        }

        if (structure.slug == 'product_category') {
          if (
            item.category &&
            item.category[key] &&
            item.category[key].length > 0
          ) {
            return item.category[key];
          }
        }

        if (structure.slug == 'item_tags') {
          if (item.tags && item.tags.length > 0) {
            for (let x = 0; x < item.tags.length; x++) {
              const tag :any = item.tags[x];
              if (tag.tag_details) {
                if (tag.tag_details[key]) {
                  return tag.tag_details[key];
                }
              }
            }
          }
        }

        if (structure.slug == 'client_tags') {
          if (client.tags && client.tags.length > 0) {
            for (let x = 0; x < client.tags.length; x++) {
              const tag :any = client.tags[x];
              if (tag[key]) {
                return tag[key];
              }
            }
          }
        }
      }
    } else {
      if (client && client[key]) {
        return client[key];
      }

      if (
        client &&
        client.client_type &&
        client.client_type[key] &&
        client.client_type[key] > 0
      ) {
        return client.client_type[key];
      }

      if (item[key] && item[key].length > 0) {
        return item[key];
      }

      if (
        item.subcategory &&
        item.subcategory[key] &&
        item.subcategory[key].length > 0
      ) {
        return item.subcategory[key];
      }

      if (item.category && item.category[key] && item.category[key].length > 0) {
        return item.category[key];
      }
    }

    return false;
  }

  onFileUpload(event) {
    const file = event.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fd = new FormData();
    fd.append('file', file, file.name);

    Swal.fire('', 'Uploading, please wait...');
    Swal.showLoading();

    // if (fileExt != 'xlsx') {
    // 	Swal.fire('Invalid file', 'Please upload a valid csv or excel file.', 'error');
    // 	this.cd.detectChanges();
    // 	return;
    // }

    Swal.fire('', 'Uploading, please wait...');
    Swal.showLoading();
    this.clientSvc.uploadClient(fd).subscribe((data :any) => {
      Swal.fire('Uploaded.', 'Uploaded successfully.');
      Swal.hideLoading();

      data.forEach((item :any) => {
        const row :any = {
          id: 0,
          item: item,
          qty: item.quantity,
          price: item.unit_price,
          discount: this.getDiscountAmount(item, item.quantity, item.line_total),
          line_total: item.line_total,
        };

        (row.discount_percent =
          (row.discount / (item.quantity * item.unit_price)) * 100),
          this.addItem(row);
      });

      this.cd.detectChanges();
    });
  }

  refreshLineItems() {
    const client :any = this.formData.value.client;
    this.price_structure =
      client && client.pricing_structure == 'markup_structure'
        ? 'Markup'
        : 'Discount';

    // copy
    const lineItems = JSON.parse(JSON.stringify(this.formData.value.line_items));

    // reset
    this.total.amount = 0;
    this.total.discount = 0;
    const control = <UntypedFormArray>this.formData.controls['line_items'];
    while (control.length > 0) {
      control.removeAt(0);
    }

    lineItems.forEach((item :any) => {
      const row :any = {
        id: item.id,
        item: item.item,
        qty: item.qty,
        price: item.price,
        discount: this.getDiscountAmount(item.item, item.qty, item.line_total),
        line_total: item.line_total,
      };
      (row.discount_percent = (row.discount / (item.qty * item.price)) * 100),
        this.addItem(row);
    });

    this.cd.detectChanges();
  }

  downloadTemplate() {
    this.clientSvc.downloadTemplate();
  }

  toggleOverride() {
    this.is_overridden = true;
  }

  resetOverride() {
    if (this.purchaseOrder && this.purchaseOrder.id) {
      this.clientPurchaseOrderSvc.resetPo(this.purchaseOrder.id).subscribe(
        (resp :any) => {
          // this.toastr.success("Purchase order updated.");
          // this.loading = false;
          // this.activeModal.close(resp);
          const lineItems = this.formData.get('line_items') as UntypedFormArray;
          while (lineItems.length !== 0) {
            const lItem = lineItems.at(0).value;
            const price =
              this.price_structure == 'Discount'
                ? lItem.price
                : this.getMaterialCost(lItem.item);
            const lineTotal :number =
              lItem.qty * price +
              lItem.discount * (this.price_structure == 'Discount' ? -1 : 1);
            this.total.amount -= lineTotal;
            this.total.discount -= lItem.discount;
            lineItems.removeAt(0);
          }
          resp.forEach((item :any) => {
            // console.log(item);
            const lineTotal :number =
              item.quantity * item.unit_price +
              item.discount_amount * (this.price_structure == 'Discount' ? -1 : 1);
            const row :any = {
              id: item.id,
              item: {
                id: item.item_id,
                is_kit: item.is_kit,
                sku: item.sku,
                product_name: item.product_name,
                discount_structure:
                  item.item_id > 0 ? item.item.discount_structure : null,
              },
              qty: item.quantity,
              price: item.unit_price,
              discount: item.discount_amount,
              discount_percent:
                (item.discount_amount / (item.quantity * item.unit_price)) * 100,
            };

            this.addItem(row);
            this.is_overridden = false;
            this.formData.patchValue({
              overridden: 0,
            });
          });
        },
        (error) => {
          // this.loading = false;
          // this.toastr.error(
          // 	"Error saving purchase order. Please try again later or contact support."
          // );
        },
      );
    } else {
      this.is_overridden = false;

      const lineItems = this.formData.get('line_items') as UntypedFormArray;

      while (lineItems.length !== 0) {
        const lItem = lineItems.at(0).value;
        const price =
          this.price_structure == 'Discount'
            ? lItem.price
            : this.getMaterialCost(lItem.item);
        const lineTotal :number =
          lItem.qty * price +
          lItem.discount * (this.price_structure == 'Discount' ? -1 : 1);
        this.total.amount -= lineTotal;
        this.total.discount -= lItem.discount;
        lineItems.removeAt(0);
      }

      this.originalLineItems.forEach((item, idx, arr) => {
        const row :any = {
          id: item.id,
          item: item.item,
          qty: item.qty,
          price: item.price,
          discount: this.getDiscountAmount(item.item, item.qty, item.line_total),
          line_total: item.line_total,
        };
        row.discount_percent = (row.discount / (item.qty * item.price)) * 100;

        this.addItem(row);
      });
    }
  }

  logLineItems() {
    console.log(this.formData.get('line_items'));
  }

  approvePo(purchaseOrder :any) {
    Swal.fire({
      title: 'Confirm approval?',
      text: 'This will approve the purchase order and mark it as ACTIVE',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Approve PO!',
    }).then((result) => {
      if (result.value) {
        this.clientPurchaseOrderSvc
          .approvePo(purchaseOrder.id)
          .subscribe((resp :any) => {
            this.toastr.success('Successfully approved purchase order.', 'Success');
          });
      }
    });
  }

  rejectPo(purchaseOrder :any) {
    Swal.fire({
      title: 'Confirm rejection?',
      text: 'This will reject this PO and mark it as DRAFT again.',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Reject',
    }).then((result) => {
      if (result.value) {
        this.clientPurchaseOrderSvc
          .rejectPo(purchaseOrder.id)
          .subscribe((resp :any) => {
            this.toastr.success('Rejected the purchase order.', 'Success');
          });
      }
    });
  }

  customTagsSearchFn(term :any, item :any) {
    term = term.toLocaleLowerCase();

    return item.name
      .toLowerCase()
      .replace(/&nbsp;/gi, '')
      .includes(term);
  }

  async loadTemplate(clientId) {
    return await this.http
      .get(`${this.url}/client-pricing-structure-setting`, {
        params: { client_id: clientId },
      })
      .toPromise()
      .then((resp :any) => {
        return resp.structure;
      });
  }

  changeClient() {
    if (this.formData.get('client').value == null) {
      this.resetAll();
    }
  }

  resetAll() {
    const dateToday = new Date();

    this.formData = this.fb.group({
      client: [null, Validators.required],
      order_date: [dateToday.toISOString().substr(0, 10), Validators.required],
      line_items: this.fb.array([]),
      ship_address: [''],
      ship_city: [''],
      ship_state: [''],
      ship_zip: [''],
      ship_country: [''],
      overridden: [0],
      will_affect_inventory: [0],
      tags: [[]],
      po_type: ['one_time'],
      is_limited_by_occurrence: [0],
      max_occurrences: [''],
      recurring_type: ['day'],
      day_weekly: [''],
      day_monthly: [''],
      day_annually: [''],
      pricing_setting: ['default'],
    });

    this.total = {
      units: 0,
      amount: 0,
      discount: 0,
    };

    this.originalLineItems = [];
  }

  getWarehouses() {
    this.locationSvc.getAll({ all: 1, parent_id: 0 }).subscribe((resp) => {
      this.warehouses = resp;
    });
  }

  changeDeliveryType(delivery_type :any) :void {
    this.formData.get('ship_country').patchValue('');
    this.formData.get('ship_zip').patchValue('');
    this.formData.get('ship_state').patchValue('');
    this.formData.get('ship_city').patchValue('');
    this.formData.get('ship_address').patchValue('');

    if (delivery_type != 'warehouse') {
      this.formData.get('warehouse_id').patchValue(0);
      this.cd.detectChanges();
    }
  }

  changeWarehouse(warehouse_id :any) :void {
    const warehouse = this.warehouses.find((row) => row.id == warehouse_id);

    if (warehouse) {
      this.formData.get('ship_country').patchValue(warehouse.country);
      this.formData.get('ship_zip').patchValue(warehouse.zip);
      this.formData.get('ship_state').patchValue(warehouse.state);
      this.formData.get('ship_city').patchValue(warehouse.city);
      this.formData.get('ship_address').patchValue(warehouse.street_address_1);

      this.cd.detectChanges();
    }
  }

  private checkValidPurchaseId(num :number) :void {
    if (isNaN(num)) {
      this.loading = false;
      this.toastr.error('PO Id is not a number. Please enter a valid id value.');
    }
  }
}
